import React, { useMemo, useState } from "react";
import { Filter, Plus, X, Search, Eye, EyeOff } from "lucide-react";
import { usePerms } from "@/lib/auth";

/**
 * FieldFilterBar — dynamic, per-field filter chips based on the user's viewable
 * fields for a module.
 *
 * Props:
 *   moduleKey       string   — e.g. "orders"
 *   fields          array    — [{ key, label, type?, opts?(dropdowns), mono? }, ...]
 *                              type ∈ { text | number | date | select | url }
 *                              select fields render as a multi-select checkbox
 *                              popover; picking N options ORs them, and an
 *                              "(blank)" checkbox is always available so ops
 *                              can include rows with an empty value in the OR
 *                              set. Value shape: { in: [...], includeBlank: bool }.
 *                              Legacy string values ("value") are still honoured.
 *   dropdowns       object   — dropdown lists (used for type="select")
 *   value           object   — current filter map { [fieldKey]: filterValue }
 *   onChange        fn       — replaces the entire filter map
 *   revealedColumns array?   — list of field keys currently shown as grid columns
 *   onToggleColumn  fn?      — (fieldKey) => toggles that field on/off in the grid.
 *                              When provided each chip renders an eye button so
 *                              ops can promote a filter into a visible column.
 */
export default function FieldFilterBar({ moduleKey, fields, dropdowns = {}, value = {}, onChange, revealedColumns, onToggleColumn, rows = [] }) {
  const { canView } = usePerms();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  // Which multi-select popover is currently open. Empty string = none.
  const [multiOpenKey, setMultiOpenKey] = useState("");
  // Per-field search text inside the multi-select popover ({fieldKey: query}).
  const [multiSearch, setMultiSearch] = useState({});

  const allowedFields = useMemo(
    () => fields.filter((f) => canView(moduleKey, f.key)),
    [fields, moduleKey, canView]
  );

  // Sep 7 2026 — for numeric qty-style filters we derive the dropdown
  // options from the row data itself (sorted, unique, numeric). Memoised
  // per-field so a large dataset (~40k rows) doesn't recompute on every
  // render. Only fields with `type: "qty"` or the legacy `"number-range"`
  // alias get this treatment.
  const qtyOptions = useMemo(() => {
    const out = {};
    if (!Array.isArray(rows) || rows.length === 0) return out;
    for (const f of allowedFields) {
      if (f.type !== "qty" && f.type !== "number-range") continue;
      const seen = new Set();
      for (const r of rows) {
        const v = r?.[f.key];
        if (v === null || v === undefined || v === "") continue;
        seen.add(String(v));
      }
      out[f.key] = [...seen].sort((a, b) => Number(a) - Number(b));
    }
    return out;
  }, [rows, allowedFields]);

  const _isInactiveValue = (v) => {
    if (v === undefined || v === null || v === "") return true;
    if (typeof v === "object") {
      // Multi-select shape: { in: [...], includeBlank: bool }
      if ("in" in v || "includeBlank" in v) {
        return (!Array.isArray(v.in) || v.in.length === 0) && !v.includeBlank;
      }
      // Number range / date range / empty toggle — inactive if every value
      // is falsy (0 counts as inactive here, which matches prior behaviour
      // for min="", max="").
      return Object.values(v).every((x) => !x);
    }
    return false;
  };

  const activeKeys = Object.keys(value).filter((k) => !_isInactiveValue(value[k]));
  // Sep 8 2026 — chip render loop iterates over EVERY key present in
  // ``value`` (including inactive/empty seeds) so a freshly-added filter
  // chip still renders and the user can type into it. ``activeKeys``
  // stays as the "does this filter change results?" set — it drives the
  // Clear counter and the applied-filter matcher.
  const chipKeys = Object.keys(value).filter((k) => allowedFields.some((f) => f.key === k));

  const setOne = (k, next) => {
    const copy = { ...value };
    if (next === "" || next === null || next === undefined) delete copy[k];
    else if (typeof next === "object" && !next.empty && _isInactiveValue(next)) delete copy[k];
    else copy[k] = next;
    onChange(copy);
  };
  const toggleEmpty = (k) => {
    const current = value[k];
    const isEmpty = typeof current === "object" && current?.empty === true;
    setOne(k, isEmpty ? "" : { empty: true });
  };
  const clearAll = () => onChange({});
  const addFilter = (fkey) => {
    // Sep 8 2026 — previously seeded a *space* string (`" "`) so the
    // chip would render, but that space also passed the "has value"
    // check inside applyFieldFilters (text branch trims to "" and
    // short-circuits, but date/select branches saw a truthy object and
    // could blank the grid). Seed with an inactive empty-string instead;
    // the chip still renders (see the `key in value` test) and the row
    // stays included until the user actually picks a value.
    onChange({ ...value, [fkey]: value[fkey] ?? "" });
    setPickerOpen(false);
    setPickerQuery("");
  };

  const notYetAdded = allowedFields.filter((f) => !(f.key in value));
  const availableFiltered = notYetAdded.filter((f) => {
    const q = pickerQuery.trim().toLowerCase();
    return !q || f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q);
  });

  if (allowedFields.length === 0) return null;

  const renderInput = (f) => {
    const v = value[f.key] ?? "";
    const isEmptyMode = typeof v === "object" && v?.empty === true;
    if (isEmptyMode) {
      return (
        <span className="text-[11px] italic text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-1.5 h-6 inline-flex items-center font-mono"
              data-testid={`filter-${moduleKey}-${f.key}-empty-active`}>
          is blank / empty
        </span>
      );
    }
    const isMultiSelect = f.type === "select" || f.type === "qty" || f.type === "number-range";
    const opts = isMultiSelect
      ? (f.type === "select"
          ? (typeof f.opts === "function" ? f.opts(dropdowns) : (f.opts || dropdowns[f.key] || []))
          : (qtyOptions[f.key] || []))
      : null;
    if (isMultiSelect) {
      // v may be a string (legacy single-select), an array of strings,
      // or { in: [...], includeBlank: bool }. Normalise for rendering.
      const selected = Array.isArray(v)
        ? { in: v, includeBlank: false }
        : (v && typeof v === "object" && (v.in || v.includeBlank !== undefined))
          ? { in: Array.isArray(v.in) ? v.in : [], includeBlank: !!v.includeBlank }
          : (typeof v === "string" && v !== "")
            ? { in: [v], includeBlank: false }
            : { in: [], includeBlank: false };
      const toggle = (opt) => {
        const cur = new Set(selected.in);
        if (cur.has(opt)) cur.delete(opt);
        else cur.add(opt);
        const next = { in: [...cur], includeBlank: selected.includeBlank };
        if (next.in.length === 0 && !next.includeBlank) {
          setOne(f.key, "");
        } else {
          setOne(f.key, next);
        }
      };
      const toggleBlank = () => {
        const next = { in: selected.in, includeBlank: !selected.includeBlank };
        if (next.in.length === 0 && !next.includeBlank) {
          setOne(f.key, "");
        } else {
          setOne(f.key, next);
        }
      };
      const isOpen = multiOpenKey === f.key;
      const label = selected.in.length === 0 && !selected.includeBlank
        ? "any"
        : selected.in.length === 0 && selected.includeBlank
          ? "(blank)"
          : selected.in.length === 1 && !selected.includeBlank
            ? selected.in[0]
            : `${selected.in.length}${selected.includeBlank ? "+∅" : ""} selected`;
      const optQuery = (multiSearch[f.key] || "").trim().toLowerCase();
      const visibleOpts = optQuery
        ? (opts || []).filter((o) => String(o).toLowerCase().includes(optQuery))
        : (opts || []);
      const visibleSelectedCount = visibleOpts.filter((o) => selected.in.includes(o)).length;
      const allVisibleSelected = visibleOpts.length > 0 && visibleSelectedCount === visibleOpts.length;
      const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;
      const toggleSelectAllVisible = (e) => {
        const cur = new Set(selected.in);
        if (e.target.checked) visibleOpts.forEach((o) => cur.add(o));
        else visibleOpts.forEach((o) => cur.delete(o));
        const next = { in: [...cur], includeBlank: selected.includeBlank };
        if (next.in.length === 0 && !next.includeBlank) setOne(f.key, "");
        else setOne(f.key, next);
      };
      return (
        <div className="relative">
          <button
            type="button"
            onClick={() => setMultiOpenKey(isOpen ? "" : f.key)}
            className="h-7 px-2 text-[12px] border-0 bg-transparent focus:outline-none min-w-[80px] text-left inline-flex items-center gap-1"
            data-testid={`filter-${moduleKey}-${f.key}`}
          >
            <span className={selected.in.length || selected.includeBlank ? "text-slate-900" : "text-slate-400"}>{label}</span>
            <span className="text-slate-400 text-[9px]">▾</span>
          </button>
          {isOpen && (
            <>
              {/* Backdrop so clicking anywhere else closes the popover. */}
              <div className="fixed inset-0 z-30" onClick={() => setMultiOpenKey("")} />
              <div
                className="absolute z-40 mt-1 left-0 min-w-[220px] max-h-72 overflow-hidden flex flex-col bg-white border border-slate-200 rounded-sm shadow-lg"
                data-testid={`filter-${moduleKey}-${f.key}-popover`}
              >
              {/* Search + Select-all — added Feb 28 2026 so ops can find long
                  lists (accounts, statuses) instantly. Behaviour matches the
                  toolbar MultiSelectFilter. */}
              <div className="relative px-2 pt-2">
                <Search size={11} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  autoFocus
                  value={multiSearch[f.key] || ""}
                  onChange={(e) => setMultiSearch((s) => ({ ...s, [f.key]: e.target.value }))}
                  placeholder="Search..."
                  data-testid={`filter-${moduleKey}-${f.key}-search`}
                  className="h-7 w-full pl-6 pr-2 text-[11.5px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <label className="flex items-center gap-2 px-3 py-1.5 mt-1.5 text-[11.5px] border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                  onChange={toggleSelectAllVisible}
                  data-testid={`filter-${moduleKey}-${f.key}-select-all`}
                />
                <span className="font-semibold text-slate-700">Select all</span>
                <span className="ml-auto font-mono text-[10px] text-slate-500">{visibleSelectedCount}/{visibleOpts.length}</span>
              </label>
              <div className="flex-1 overflow-auto py-1">
                <label className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 cursor-pointer text-[11.5px]">
                  <input
                    type="checkbox"
                    checked={selected.includeBlank}
                    onChange={toggleBlank}
                    data-testid={`filter-${moduleKey}-${f.key}-blank`}
                  />
                  <span className="italic text-amber-800">(blank / empty)</span>
                </label>
                <div className="border-t border-slate-100 my-1" />
                {visibleOpts.length === 0 && (
                  <div className="px-2 py-2 text-[11px] text-slate-400 italic">No matches</div>
                )}
                {visibleOpts.map((o) => (
                  <label key={o} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 cursor-pointer text-[11.5px]">
                    <input
                      type="checkbox"
                      checked={selected.in.includes(o)}
                      onChange={() => toggle(o)}
                      data-testid={`filter-${moduleKey}-${f.key}-opt-${o}`}
                    />
                    <span>{o}</span>
                  </label>
                ))}
                {/* Sep 7 2026 — high-cardinality fields (china_id > 5000
                    distinct values, comments, marketplace_identifier)
                    are truncated at load time. When the user types
                    something that isn't in the visible list, offer a
                    one-click "Add '<typed>'" so the exact value can
                    still be applied as an $in filter without waiting
                    for a server-side search round-trip. */}
                {(() => {
                  const typed = (multiSearch[f.key] || "").trim();
                  if (!typed) return null;
                  const inOpts = opts.map(String).includes(typed);
                  const alreadySelected = selected.in.map(String).includes(typed);
                  if (inOpts || alreadySelected) return null;
                  return (
                    <button
                      type="button"
                      onClick={() => {
                        toggle(typed);
                        setMultiSearch((s) => ({ ...s, [f.key]: "" }));
                      }}
                      className="flex items-center gap-2 px-2 py-1 w-full text-left hover:bg-blue-50 text-[11.5px] text-blue-700 font-semibold border-t border-slate-100"
                      data-testid={`filter-${moduleKey}-${f.key}-add-custom`}
                    >
                      + Use "{typed}"
                    </button>
                  );
                })()}
              </div>
              {/* Sep 7 2026 — Clear + Done footer so ops can commit or reset
                  the selection without hunting for a click-outside. Matches
                  the shared MultiSelectFilter footer in the toolbar. */}
              <div className="flex items-center gap-2 border-t border-slate-100 px-2 py-1.5 bg-slate-50">
                <button
                  type="button"
                  onClick={() => { setOne(f.key, ""); }}
                  className="text-[11px] text-slate-600 hover:text-red-700 hover:underline font-semibold"
                  data-testid={`filter-${moduleKey}-${f.key}-clear`}
                >Clear</button>
                <button
                  type="button"
                  onClick={() => setMultiOpenKey("")}
                  className="ml-auto text-[11px] px-2.5 py-0.5 bg-slate-900 text-white rounded-sm hover:bg-slate-700 font-semibold"
                  data-testid={`filter-${moduleKey}-${f.key}-done`}
                >Done</button>
              </div>
              </div>
            </>
          )}
        </div>
      );
    }
    if (f.type === "date") {
      const obj = typeof v === "object" ? v : {};
      return (
        <div className="flex items-center gap-1">
          <input
            type="date"
            value={obj.from || ""}
            onChange={(e) => setOne(f.key, { ...obj, from: e.target.value })}
            className="h-7 px-1.5 text-[11.5px] border-0 bg-transparent focus:outline-none font-mono"
            data-testid={`filter-${moduleKey}-${f.key}-from`}
          />
          <span className="text-slate-400 text-[10px]">→</span>
          <input
            type="date"
            value={obj.to || ""}
            onChange={(e) => setOne(f.key, { ...obj, to: e.target.value })}
            className="h-7 px-1.5 text-[11.5px] border-0 bg-transparent focus:outline-none font-mono"
            data-testid={`filter-${moduleKey}-${f.key}-to`}
          />
        </div>
      );
    }
    if (f.type === "number") {
      const obj = typeof v === "object" ? v : {};
      return (
        <div className="flex items-center gap-1">
          <input
            type="number"
            placeholder="min"
            value={obj.min ?? ""}
            onChange={(e) => setOne(f.key, { ...obj, min: e.target.value })}
            className="h-7 w-20 px-1.5 text-[12px] border-0 bg-transparent focus:outline-none font-mono"
            data-testid={`filter-${moduleKey}-${f.key}-min`}
          />
          <span className="text-slate-400 text-[10px]">–</span>
          <input
            type="number"
            placeholder="max"
            value={obj.max ?? ""}
            onChange={(e) => setOne(f.key, { ...obj, max: e.target.value })}
            className="h-7 w-20 px-1.5 text-[12px] border-0 bg-transparent focus:outline-none font-mono"
            data-testid={`filter-${moduleKey}-${f.key}-max`}
          />
        </div>
      );
    }
    // default: text
    return (
      <input
        type="text"
        value={typeof v === "string" ? v.trim() === "" ? "" : v : ""}
        onChange={(e) => setOne(f.key, e.target.value)}
        placeholder="contains…"
        className="h-7 px-2 text-[12px] border-0 bg-transparent focus:outline-none min-w-[110px]"
        data-testid={`filter-${moduleKey}-${f.key}`}
      />
    );
  };

  return (
    <div className="border border-slate-200 bg-white rounded-sm p-2 flex flex-wrap items-center gap-2" data-testid={`filter-bar-${moduleKey}`}>
      <Filter size={13} className="text-slate-500 ml-1" />
      {chipKeys.map((k) => {
        const f = allowedFields.find((x) => x.key === k) || { key: k, label: k, type: "text" };
        const isEmptyMode = typeof value[k] === "object" && value[k]?.empty === true;
        const isRevealed = Array.isArray(revealedColumns) && revealedColumns.includes(k);
        return (
          <div key={k} className="inline-flex items-center gap-0.5 bg-slate-100 border border-slate-200 rounded-sm pl-2 pr-0.5">
            <span className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">{f.label}</span>
            <span className="text-slate-300 text-[10px]">|</span>
            {renderInput(f)}
            {onToggleColumn && (
              <button
                onClick={() => onToggleColumn(k)}
                className={`w-5 h-5 flex items-center justify-center rounded-sm ${isRevealed ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-indigo-800 hover:bg-white"}`}
                data-testid={`filter-reveal-${moduleKey}-${k}`}
                title={isRevealed ? "Column visible — click to hide" : "Show as a column in the grid"}
              >
                {isRevealed ? <Eye size={11} /> : <EyeOff size={11} />}
              </button>
            )}
            <button
              onClick={() => toggleEmpty(k)}
              className={`w-5 h-5 flex items-center justify-center rounded-sm text-[13px] font-bold ${isEmptyMode ? "bg-amber-600 text-white" : "text-slate-500 hover:text-amber-800 hover:bg-white"}`}
              data-testid={`filter-empty-${moduleKey}-${k}`}
              title={isEmptyMode ? "Empty-only mode — click to disable" : "Match only blank/empty values"}
            >∅</button>
            <button
              onClick={() => setOne(k, undefined)}
              className="w-5 h-5 flex items-center justify-center text-slate-500 hover:text-red-700 hover:bg-white rounded-sm"
              data-testid={`filter-remove-${moduleKey}-${k}`}
              title="Remove filter"
            >
              <X size={11} />
            </button>
          </div>
        );
      })}
      <div className="relative">
        <button
          onClick={() => setPickerOpen((s) => !s)}
          className="h-7 px-2 text-[11.5px] font-semibold text-slate-700 border border-dashed border-slate-300 rounded-sm hover:bg-slate-50 inline-flex items-center gap-1"
          data-testid={`filter-add-${moduleKey}`}
          disabled={notYetAdded.length === 0}
        >
          <Plus size={11} /> Add filter
          {notYetAdded.length > 0 && <span className="ml-1 text-[10px] text-slate-400 font-mono">({notYetAdded.length})</span>}
        </button>
        {pickerOpen && notYetAdded.length > 0 && (
          <div className="absolute z-30 mt-1 left-0 w-64 bg-white border border-slate-200 rounded-sm shadow-lg" onMouseLeave={() => setPickerOpen(false)}>
            <div className="p-2 border-b border-slate-200">
              <div className="relative">
                <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  autoFocus
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  placeholder="Search fields…"
                  className="w-full h-7 pl-6 pr-2 text-[12px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  data-testid={`filter-picker-search-${moduleKey}`}
                />
              </div>
            </div>
            <div className="max-h-64 overflow-auto">
              {availableFiltered.map((f) => (
                <button
                  key={f.key}
                  onClick={() => addFilter(f.key)}
                  className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-slate-50 flex items-center justify-between"
                  data-testid={`filter-picker-${moduleKey}-${f.key}`}
                >
                  <span>{f.label}</span>
                  <span className="text-[9.5px] font-mono text-slate-400 uppercase">{f.type || "text"}</span>
                </button>
              ))}
              {!availableFiltered.length && <div className="p-3 text-[11.5px] text-slate-500 text-center">No matching fields</div>}
            </div>
          </div>
        )}
      </div>
      {activeKeys.length > 0 && (
        <button
          onClick={clearAll}
          className="ml-auto text-[11px] text-blue-600 hover:underline"
          data-testid={`filter-clear-${moduleKey}`}
        >
          Clear {activeKeys.length}
        </button>
      )}
    </div>
  );
}

/**
 * applyFieldFilters — client-side matcher used by callers. Row-level.
 * Returns true if the row satisfies every active filter.
 */

// Sep 8 2026 — flexible date parser used by the date-type filter branch.
// Understands every format the ERP stores: ISO ``YYYY-MM-DD`` (with or
// without a T… suffix), ``DD/MM/YY``, ``DD/MM/YYYY``, ``DD-Mon-YYYY``
// (e.g. ``04-Sep-2025``), ``DD-MM-YYYY`` (e.g. ``31-08-2026``) and
// ``DD-Mon-YY`` (e.g. ``24-Aug-26``). Returns a UTC-noon epoch (ms) so
// timezone drift can't shift a bare date into the previous day; returns
// ``null`` for unparseable input so callers can drop the row instead of
// throwing.
const _MONTH_ABBR = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};
function _dateFromParts(y, m, d) {
  if (y < 100) y += y >= 70 ? 1900 : 2000;      // "26" → 2026, "99" → 1999
  const t = Date.UTC(y, m, d, 12, 0, 0);          // noon UTC
  return Number.isNaN(t) ? null : t;
}
export function _parseDateFlexible(v) {
  if (v === null || v === undefined || v === "") return null;
  const s = String(v).trim();
  if (!s) return null;
  // ISO: 2025-09-04 or 2025-09-04T14:23:00[Z]
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (m) return _dateFromParts(+m[1], +m[2] - 1, +m[3]);
  // DD-Mon-YYYY / DD-Mon-YY: 04-Sep-2025, 24-Aug-26
  m = /^(\d{1,2})[-/\s]([A-Za-z]{3,})[-/\s](\d{2}|\d{4})$/.exec(s);
  if (m) {
    const mo = _MONTH_ABBR[m[2].slice(0, 3).toLowerCase()];
    if (mo === undefined) return null;
    return _dateFromParts(+m[3], mo, +m[1]);
  }
  // DD-MM-YYYY / DD/MM/YYYY / DD/MM/YY / DD-MM-YY
  m = /^(\d{1,2})[-/](\d{1,2})[-/](\d{2}|\d{4})$/.exec(s);
  if (m) return _dateFromParts(+m[3], +m[2] - 1, +m[1]);
  // Last-ditch: let the runtime parser have a go.
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : t;
}

export function applyFieldFilters(row, filters, fields) {
  const idx = Object.fromEntries((fields || []).map((f) => [f.key, f]));
  for (const [key, v] of Object.entries(filters || {})) {
    if (v === undefined || v === null || v === "") continue;
    const spec = idx[key] || { type: "text" };
    const cell = row?.[key];
    // Special "match empty/blank" mode set via the ∅ toggle
    if (typeof v === "object" && v?.empty === true) {
      const isBlank = cell === undefined || cell === null || cell === ""
                     || (Array.isArray(cell) && cell.length === 0);
      if (!isBlank) return false;
      continue;
    }
    if (spec.type === "select" || spec.type === "qty" || spec.type === "number-range") {
      // Value can be: string (legacy single), array of strings (multi),
      // or { in: [...], includeBlank: bool } (new). All three are honoured.
      let inList = null;
      let includeBlank = false;
      if (typeof v === "string") {
        inList = v ? [v] : [];
      } else if (Array.isArray(v)) {
        inList = v;
      } else if (v && typeof v === "object") {
        inList = Array.isArray(v.in) ? v.in : [];
        includeBlank = !!v.includeBlank;
      }
      if (!inList) continue;
      if (inList.length === 0 && !includeBlank) continue;
      const isBlank = cell === undefined || cell === null || cell === ""
                     || (Array.isArray(cell) && cell.length === 0);
      if (isBlank) {
        if (!includeBlank) return false;
        continue;
      }
      const optsLc = inList.map((opt) => String(opt).toLowerCase());
      const cellVals = Array.isArray(cell) ? cell.map((c) => String(c).toLowerCase()) : [String(cell).toLowerCase()];
      const matched = cellVals.some((cv) => optsLc.includes(cv));
      if (!matched) return false;
    } else if (spec.type === "date") {
      const obj = typeof v === "object" ? v : {};
      if (!obj.from && !obj.to) continue;
      if (!cell) return false;
      // Sep 8 2026 — parse both the cell + the bounds into real dates
      // (numeric epoch) before comparing. The previous 10-char slice +
      // string compare only worked for ISO YYYY-MM-DD; for
      // ``04-Sep-2025`` / ``31-08-2026`` / ``24-Aug-26`` / ``DD/MM/YY``
      // it blanked the whole grid because the lexical compare put the
      // day first. Unparseable values fall through as "no match" so a
      // malformed cell never throws.
      const cellMs = _parseDateFlexible(cell);
      if (cellMs === null) return false;
      if (obj.from) {
        const fromMs = _parseDateFlexible(obj.from);
        if (fromMs !== null && cellMs < fromMs) return false;
      }
      if (obj.to) {
        const toMs = _parseDateFlexible(obj.to);
        // Interpret `to` as inclusive end-of-day so a `to = 2025-09-04`
        // filter still includes a `2025-09-04T18:00` timestamp cell.
        if (toMs !== null && cellMs > toMs + 86_399_999) return false;
      }
    } else if (spec.type === "number") {
      const obj = typeof v === "object" ? v : {};
      if (obj.min === "" && obj.max === "") continue;
      const n = Number(cell);
      if (Number.isNaN(n)) return false;
      if (obj.min !== "" && obj.min !== undefined && n < Number(obj.min)) return false;
      if (obj.max !== "" && obj.max !== undefined && n > Number(obj.max)) return false;
    } else {
      // text contains — case-insensitive, whitespace-normalised
      const needle = String(v).trim().toLowerCase();
      if (!needle) continue;
      const hay = String(cell ?? "").toLowerCase();
      if (!hay.includes(needle)) return false;
    }
  }
  return true;
}
